/**
 * Hybrid Logical Clock — ARCHITECTURE.md §4.2, ADR-002.
 *
 * Stamp wire format (opaque to everyone but this module):
 *
 *   <ISO-8601 UTC, ms precision, fixed 24 chars>-<counter, 4 chars base36>-<client_id>
 *   e.g. "2026-07-01T12:34:56.789Z-0003-k7f2q9ab"
 *
 * The fixed-width layout happens to be lexicographically sortable, but that is
 * incidental: CONSTRAINTS #4 BANS raw string comparison of timestamps (v1's
 * `_newer()` string compare caused real data loss). ALL ordering must go through
 * `compareHLC()` below, which parses and compares numerically:
 * wall time, then counter, then client_id (deterministic tiebreak — §4.2).
 *
 * This file has ZERO imports on purpose: it runs identically in Node (server,
 * Sheet reconcile, CLI) and the browser (client optimistic path / outbox).
 */

/** An encoded HLC stamp. Treat as opaque; never compare as a raw string (CONSTRAINTS #4). */
export type HLC = string;

export interface ParsedHLC {
  /** Wall-clock component, ms since epoch, UTC. */
  wallMs: number;
  /** Logical counter for same-ms causality. 0..MAX_HLC_COUNTER. */
  counter: number;
  /** Stable per-device/per-actor id — the final deterministic tiebreak. */
  clientId: string;
}

const COUNTER_WIDTH = 4;
export const MAX_HLC_COUNTER = 36 ** COUNTER_WIDTH - 1; // 1_679_615 ticks per ms

/**
 * Server clamp tolerance (§4.2: "server clamps any stamp beyond server_now + epsilon").
 * NOTE: the docs fix the mechanism but not the value; 5 minutes tolerates ordinary
 * device clock skew while making it impossible to freeze an item in the far future.
 */
export const DEFAULT_CLAMP_EPSILON_MS = 5 * 60_000;

// ISO (24 chars, strict) - counter (4 base36) - clientId (no "-", so parsing is unambiguous).
const HLC_RE =
  /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z)-([0-9a-z]{4})-([A-Za-z0-9_.]{1,64})$/;

export class HLCParseError extends Error {
  constructor(raw: string) {
    super(`invalid HLC stamp: ${JSON.stringify(raw)}`);
    this.name = "HLCParseError";
  }
}

/** Parse a stamp or return null. Never throws. */
export function tryParseHLC(hlc: string): ParsedHLC | null {
  if (typeof hlc !== "string") return null;
  const m = HLC_RE.exec(hlc);
  if (m === null) return null;
  const iso = m[1] as string;
  const counterRaw = m[2] as string;
  const clientId = m[3] as string;
  const wallMs = Date.parse(iso);
  if (Number.isNaN(wallMs)) return null; // e.g. month 13 — regex-shaped but not a real instant
  // Round-trip guard: reject non-canonical instants (Date.parse would silently normalize).
  if (new Date(wallMs).toISOString() !== iso) return null;
  return { wallMs, counter: parseInt(counterRaw, 36), clientId };
}

/** Parse a stamp or throw HLCParseError. */
export function parseHLC(hlc: string): ParsedHLC {
  const parsed = tryParseHLC(hlc);
  if (parsed === null) throw new HLCParseError(hlc);
  return parsed;
}

export function isHLC(hlc: string): boolean {
  return tryParseHLC(hlc) !== null;
}

export function encodeHLC(parsed: ParsedHLC): HLC {
  const { wallMs, counter, clientId } = parsed;
  if (!Number.isInteger(wallMs) || wallMs < 0) {
    throw new RangeError(`HLC wallMs out of range: ${wallMs}`);
  }
  if (!Number.isInteger(counter) || counter < 0 || counter > MAX_HLC_COUNTER) {
    throw new RangeError(`HLC counter out of range: ${counter}`);
  }
  if (!/^[A-Za-z0-9_.]{1,64}$/.test(clientId)) {
    throw new RangeError(`HLC clientId invalid: ${JSON.stringify(clientId)}`);
  }
  const iso = new Date(wallMs).toISOString();
  const c = counter.toString(36).padStart(COUNTER_WIDTH, "0");
  return `${iso}-${c}-${clientId}`;
}

/**
 * THE ordering function (CONSTRAINTS #4: every comparison goes through here).
 * Deterministic total order: wall time, then counter, then client_id (§4.2).
 * Returns -1 | 0 | 1. Throws HLCParseError on malformed input — callers that
 * handle untrusted ops must tryParseHLC first (applyOps parks invalid stamps).
 */
export function compareHLC(a: HLC | ParsedHLC, b: HLC | ParsedHLC): -1 | 0 | 1 {
  const pa = typeof a === "string" ? parseHLC(a) : a;
  const pb = typeof b === "string" ? parseHLC(b) : b;
  if (pa.wallMs !== pb.wallMs) return pa.wallMs < pb.wallMs ? -1 : 1;
  if (pa.counter !== pb.counter) return pa.counter < pb.counter ? -1 : 1;
  // clientId is an identifier tiebreak, not a timestamp — code-unit order is
  // deterministic and locale-free, which is all a tiebreak needs.
  if (pa.clientId !== pb.clientId) return pa.clientId < pb.clientId ? -1 : 1;
  return 0;
}

/** Generate a stable random client id (8 chars base36). Works in Node and browsers. */
export function newClientId(): string {
  const bytes = new Uint8Array(8);
  globalThis.crypto.getRandomValues(bytes);
  let out = "";
  for (const b of bytes) out += (b % 36).toString(36);
  return out;
}

/**
 * The clock a device stamps its ops with (client stamps at edit time — offline-correct).
 * `stamp()` is the HLC send rule; `observe()` is the receive rule — feed it every
 * remote stamp you apply so local stamps are always causally after everything seen.
 */
export class HLCClock {
  public readonly clientId: string;
  private readonly now: () => number;
  private lastWallMs = 0;
  private counter = 0;

  constructor(clientId: string, now: () => number = Date.now) {
    if (!/^[A-Za-z0-9_.]{1,64}$/.test(clientId)) {
      throw new RangeError(`HLCClock clientId invalid: ${JSON.stringify(clientId)}`);
    }
    this.clientId = clientId;
    this.now = now;
  }

  /** Mint a fresh stamp, strictly greater (per compareHLC) than any previous local stamp or observed remote stamp. */
  stamp(): HLC {
    const phys = Math.max(0, this.now());
    if (phys > this.lastWallMs) {
      this.lastWallMs = phys;
      this.counter = 0;
    } else {
      this.counter += 1;
      if (this.counter > MAX_HLC_COUNTER) {
        this.lastWallMs += 1;
        this.counter = 0;
      }
    }
    return encodeHLC({ wallMs: this.lastWallMs, counter: this.counter, clientId: this.clientId });
  }

  /** HLC receive rule: advance past a remote stamp so future local stamps compare greater. */
  observe(remote: HLC | ParsedHLC): void {
    const r = typeof remote === "string" ? parseHLC(remote) : remote;
    const phys = Math.max(0, this.now());
    const wall = Math.max(this.lastWallMs, r.wallMs, phys);
    let counter: number;
    if (wall === this.lastWallMs && wall === r.wallMs) {
      counter = Math.max(this.counter, r.counter) + 1;
    } else if (wall === this.lastWallMs) {
      counter = this.counter + 1;
    } else if (wall === r.wallMs) {
      counter = r.counter + 1;
    } else {
      counter = 0;
    }
    if (counter > MAX_HLC_COUNTER) {
      this.lastWallMs = wall + 1;
      this.counter = 0;
    } else {
      this.lastWallMs = wall;
      this.counter = counter;
    }
  }
}

/**
 * Server clamp (§4.2): a stamp whose wall component is beyond `serverNowMs + epsilonMs`
 * is re-encoded at the limit (counter + clientId preserved), so a clock-skewed device
 * can never freeze an item in the future. Pure, single-stamp.
 *
 * CAUTION: this function alone is NOT order-preserving across stamps — every
 * over-limit stamp lands at exactly the limit, and HLCClock resets its counter
 * each new ms, so successive edits from one skewed device collapse to IDENTICAL
 * stamps (ties keep the existing value in mergeFields → later edits go stale).
 * The server must clamp through ONE `HLCClamper` instance instead (below),
 * which keeps clamped stamps strictly increasing in encounter order.
 */
export function clampHLC(
  hlc: HLC,
  serverNowMs: number,
  epsilonMs: number = DEFAULT_CLAMP_EPSILON_MS,
): { hlc: HLC; clamped: boolean } {
  const p = parseHLC(hlc);
  const limit = serverNowMs + epsilonMs;
  if (p.wallMs <= limit) return { hlc, clamped: false };
  return {
    hlc: encodeHLC({ wallMs: limit, counter: p.counter, clientId: p.clientId }),
    clamped: true,
  };
}

/**
 * Stateful, monotonic server clamp — what the server actually uses (§4.2/§4.3).
 *
 * Same rule as clampHLC for the limit, plus a floor: every CLAMPED stamp is
 * re-encoded strictly greater (per compareHLC) than every previously clamped
 * stamp this instance emitted, preserving relative (encounter = outbox) order.
 * So a >epsilon-skewed device replaying create+edit+edit in one batch keeps its
 * own edit ordering — the later edit still wins LWW over the earlier one —
 * instead of all three collapsing to one identical stamp and parking the edits
 * as stale (the bug tools/simulation models with uniqueClampedStamp).
 *
 * In-range stamps pass through untouched and never move the floor: honest
 * stamps are already unique per (clientId, HLCClock) by construction. The
 * floor only ever nudges clamped stamps FORWARD by counter ticks (1/1.7M of a
 * ms each), so a clamped stamp stays ~epsilon ahead of server now — it can
 * still never freeze an item against honest future edits.
 *
 * The server holds ONE instance for its process lifetime so ordering also
 * holds across batches that land within the same server millisecond.
 */
export class HLCClamper {
  private floor: ParsedHLC | null = null;
  private readonly epsilonMs: number;

  // No parameter property: Node 24 strip-only mode rejects that syntax.
  constructor(epsilonMs: number = DEFAULT_CLAMP_EPSILON_MS) {
    this.epsilonMs = epsilonMs;
  }

  clamp(hlc: HLC, serverNowMs: number): { hlc: HLC; clamped: boolean } {
    const p = parseHLC(hlc);
    const limit = serverNowMs + this.epsilonMs;
    if (p.wallMs <= limit) return { hlc, clamped: false };
    let candidate: ParsedHLC = { wallMs: limit, counter: p.counter, clientId: p.clientId };
    if (this.floor !== null && compareHLC(candidate, this.floor) <= 0) {
      // Bump just past the floor (counter tick; roll into wall on overflow —
      // same overflow rule as HLCClock.stamp).
      candidate =
        this.floor.counter >= MAX_HLC_COUNTER
          ? { wallMs: this.floor.wallMs + 1, counter: 0, clientId: p.clientId }
          : { wallMs: this.floor.wallMs, counter: this.floor.counter + 1, clientId: p.clientId };
    }
    this.floor = candidate;
    return { hlc: encodeHLC(candidate), clamped: true };
  }
}
